import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ActionButton, TopHead, IconWrap, NotificationWrap, Title } from './styled';
import Icon from './Icon';

import { Notification as NotificationInterface, NotificationType } from '@store/notifications/types';
import { Subtitle } from '@components/Typography';
import { removeNotificationActionCreator } from '@store/notifications/actions';
import { getOperationUuid } from '@utils/localStorage';

interface Props {
  notification: NotificationInterface;
}

const delay = 3000;
const animationDuration = 500;

const Notification: React.FunctionComponent<Props> = ({ notification }) => {
  const { t } = useTranslation();
  const visibilityTimeout = useRef<NodeJS.Timeout>();
  const removalTimeout = useRef<NodeJS.Timeout>();
  const dispatch = useDispatch();
  const operationId = getOperationUuid();
  const [isVisible, setIsVisible] = useState(true);

  const remove = useCallback((immediate?: boolean) => {
    visibilityTimeout.current = setTimeout(
      () => {
        setIsVisible(false);
      },
      immediate ? 0 : delay - animationDuration
    );
    removalTimeout.current = setTimeout(
      () => {
        dispatch(removeNotificationActionCreator(notification.id));
      },
      immediate ? animationDuration : delay
    );
  }, []);

  useEffect(() => {
    remove();
  }, []);

  return (
    <NotificationWrap isVisible={isVisible} type={notification.type} animationDuration={animationDuration}>
      <TopHead>
        <div>
          <Title>
            <IconWrap>
              <Icon type={notification.type} />
            </IconWrap>
            {notification.title}
          </Title>
        </div>
        {!!notification.action && (
          <ActionButton
            onClick={async () => {
              clearTimeout(visibilityTimeout.current!);
              clearTimeout(removalTimeout.current!);
              const isRemove = await notification.action?.onClick!();
              if (isRemove) remove(true);
            }}
          >
            {notification.action.title}
          </ActionButton>
        )}
      </TopHead>
      {notification?.type === NotificationType.Error && (
        <Subtitle size="small" style={{ marginLeft: '7%' }}>
          {t('validation.error.notification')}
          {operationId}
        </Subtitle>
      )}
    </NotificationWrap>
  );
};

export default Notification;
